/**
 * Optimized App Bundle with Progressive Loading
 * Critical path optimized for sub-600ms JavaScript execution
 */

import { createApp } from 'vue'
import store from '~/store'
import router from '~/router'
import i18n, { initI18n } from '~/plugins/i18n'
import App from '~/components/App.vue'
import Child from '~/components/Child.vue'
import logger from '~/utils/logger'

// Critical styles only
import '~sass/app.scss'

// Critical utilities only
import lcpOptimizer from '~/utils/lcpOptimizer.js'
import scriptLoader from '~/utils/scriptLoader.js'

// Performance tracking
const startTime = performance.now()

const app = createApp(App)

// Configure app with critical plugins only
app.use(store)
app.use(router)
app.use(i18n)

// Register critical components synchronously before mounting
app.component('Child', Child)

// Track performance
store.dispatch('app/recordPerformanceMetric', {
  metric: 'appLoadTime',
  value: performance.now() - startTime
})

// Global error handler
app.config.errorHandler = (error, instance, info) => {
  logger.error('Vue Error:', error)
  logger.error('Component:', instance)
  logger.error('Info:', info)
}

// Initialize LCP optimizer immediately
lcpOptimizer.init()

// Make script loader globally available
window.scriptLoader = scriptLoader

// Wait for both router and i18n to be ready before mounting
// This ensures lazy-loaded route components and translations are resolved before rendering
Promise.all([
  router.isReady(),
  initI18n()
]).then(() => {
  app.mount('#web_app')

  // Initialize dynamic QR handler after app is mounted
  // This prevents blocking router.isReady() and allows loading screen to animate
  import('~/utils/dynamicQRHandler.js').then(module => {
    module.default.init().catch(err => {
      logger.warn('Dynamic QR Handler initialization failed:', err)
    })
  })
}).catch(error => {
  logger.error('App initialization failed:', error)
})

// Progressive loading of non-critical dependencies
const loadNonCriticalDependencies = async () => {
  try {
    // Load UI libraries
    await loadUILibraries()
    
    // Load third-party services
    await loadThirdPartyServices()
    
    // Load utilities
    await loadUtilities()
    
    // Mark as complete
    store.dispatch('app/markNonCriticalDepsLoaded')

  } catch (error) {
    logger.warn('Non-critical dependencies failed to load:', error)
  }
}

// UI Libraries (FontAwesome, Bootstrap)
const loadUILibraries = async () => {
  const { FontAwesomeIcon } = await import('~/plugins/fontawesome')
  app.component('FontAwesome', FontAwesomeIcon)
  app.component('FontAwesomeIcon', FontAwesomeIcon)

  // Create a simple Scrollactive component fallback
  app.component('Scrollactive', {
    template: '<div><slot></slot></div>',
    emits: ['itemchanged'],
    props: {
      activeClass: { type: String, default: 'active' },
      offset: { type: Number, default: 20 },
      alwaysTrack: { type: Boolean, default: true },
      duration: { type: Number, default: 600 },
      clickToScroll: { type: Boolean, default: true },
      bezierEasingValue: { type: String, default: '.5,0,.35,1' }
    }
  })

  // Load Bootstrap only when needed
  if (document.querySelector('[data-bs-toggle]')) {
    await import('bootstrap')
  }

  // Child component is now registered synchronously on app initialization
  // No need to load it here anymore
}

// Third-party services
const loadThirdPartyServices = async () => {
  // Load analytics after initial render
  const gaPlugin = await import('~/plugins/ga')
  app.use(gaPlugin.default)
  
  // Load Clarity service
  const { ClarityPlugin } = await import('~/services/clarity')
  app.use(ClarityPlugin)
  
  // Load axios
  await import('~/plugins/axios')
}

// Utility libraries and QR system
const loadUtilities = async () => {
  // Load toast notifications system
  const iziToast = await import('izitoast')
  await import('izitoast/dist/css/iziToast.min.css')
  
  // Configure toast globally
  app.config.globalProperties.$toast = {
    success: (message, options = {}) => {
      iziToast.default.success({
        message,
        position: 'topRight',
        timeout: 3000,
        ...options
      })
    },
    error: (message, options = {}) => {
      iziToast.default.error({
        message,
        position: 'topRight',
        timeout: 5000,
        ...options
      })
    },
    warning: (message, options = {}) => {
      iziToast.default.warning({
        message,
        position: 'topRight',
        timeout: 4000,
        ...options
      })
    },
    info: (message, options = {}) => {
      iziToast.default.info({
        message,
        position: 'topRight',
        timeout: 3000,
        ...options
      })
    }
  }
  
  // Global toast function
  window.showToast = (message, type = 'info') => {
    return app.config.globalProperties.$toast[type](message)
  }
  
  // Load QR system
  const [dynamicQRHandler, antiSharingProtection] = await Promise.all([
    import('~/utils/dynamicQRHandler.js'),
    import('~/utils/antiSharingProtection.js')
  ])
  
  await dynamicQRHandler.default.init()
  dynamicQRHandler.default.initURLMonitoring()
  antiSharingProtection.default.init()

  logger.log('Dynamic QR Handler initialized successfully')
}

// Load Vue plugins on demand
window.loadVuePlugins = async () => {
  const [
    VueScrollTo,
    VueSplide,
    Vue3TouchEvents
  ] = await Promise.all([
    import('vue-scrollto'),
    import('@splidejs/vue-splide'),
    import('vue3-touch-events')
  ])
  
  app.use(VueScrollTo.default)
  app.use(VueSplide.default)
  app.use(Vue3TouchEvents.default)
  
  return { VueScrollTo, VueSplide, Vue3TouchEvents }
}

// Load jQuery when absolutely needed - now using smart script loader
window.loadJQuery = async () => {
  return await scriptLoader.loadJQuery()
}

// Start loading non-critical dependencies after initial render
requestIdleCallback(() => {
  loadNonCriticalDependencies()
}, { timeout: 1000 })

// Export app for debugging
window.app = app